export const SANITIZE_FINANCE_STORAGE_KEY = 'lctf.sanitizeFinance';

export const KNOWN_MONEY_CLASSES = [
  'cost-value',
  'profit-value',
  'breakdown-price',
  'fin-cell-money',
  'po-col-amount',
  'ce-amount',
  'grand-total-amount',
  'total-value',
  'total-amount',
  'value-amount',
  'payment-log-amount',
  'payment-item-amount',
  'aa-cost-chip',
  'people-travel-option-price',
  'supplier-dropdown-price',
  'srfq-quote-history-price',
  'vp-supplier-quote-row-price',
  'vp-quote-history-price',
  'proposal-phase-card-amount',
  'investment-discount-amount',
  'summary-discount-amount',
  'species-price',
  'item-total',
  'total-psf-badge',
  'bp-tip-amt',
  'fin-kpi-value',
  'currency-symbol',
] as const;

export const KNOWN_PERCENT_CLASSES = [
  'margin-display',
  'prm-margin-cell',
  'sa-ppl-row-pct',
  'profit-percent',
  'bp-tip-pct',
] as const;

const SKIP_TAGS = new Set([
  'SCRIPT',
  'STYLE',
  'NOSCRIPT',
  'TEXTAREA',
  'INPUT',
  'SELECT',
  'OPTION',
  'CODE',
  'PRE',
]);

const SKIP_CLOSEST =
  'input, textarea, select, option, script, style, noscript, code, pre, [contenteditable="true"], [data-sanitize-ignore], .progress, .progress-bar, .percent-complete, .pct-complete, .task-progress, .portal-progress';

const CURRENCY_TOKEN =
  /(?:USD\s+)?(?:-|\u2212|\u2013|\u2014)?\s*\(?\s*\$\s*\d[\d,]*(?:\.\d+)?\s*[kKmMbB]?\s*\)?/i;
const PERCENT_TOKEN = /(?:[+\-]|\u2212)?\s*\(?\s*\d+(?:\.\d+)?\s*%\s*\)?/;
const HAS_CURRENCY = /(?:USD\s+)?\$\s*\d/i;
const HAS_PERCENT = /(?<![A-Za-z])\d+(?:\.\d+)?\s*%/;

let sanitizedFlag = false;

function readSessionFlag(): boolean {
  try {
    return sessionStorage.getItem(SANITIZE_FINANCE_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

if (typeof window !== 'undefined') {
  sanitizedFlag = readSessionFlag();
}

export function isFinanceSanitized(): boolean {
  return sanitizedFlag;
}

export function setFinanceSanitizedFlag(enabled: boolean): void {
  sanitizedFlag = enabled;
}

export function readSanitizeFinanceSession(): boolean {
  return readSessionFlag();
}

export function writeSanitizeFinanceSession(enabled: boolean): void {
  try {
    if (enabled) sessionStorage.setItem(SANITIZE_FINANCE_STORAGE_KEY, '1');
    else sessionStorage.removeItem(SANITIZE_FINANCE_STORAGE_KEY);
  } catch {
    /* private mode / SSR */
  }
}

export function applySanitizeFinanceDocumentFlag(enabled: boolean): void {
  if (typeof document === 'undefined') return;
  if (enabled) document.documentElement.dataset.sanitizeFinance = '1';
  else delete document.documentElement.dataset.sanitizeFinance;
}

export function maskCurrency(): string {
  return '****';
}

export function maskPercent(): string {
  return '**%';
}

export function formatUsd(value: number, options?: Intl.NumberFormatOptions): string {
  if (sanitizedFlag) return maskCurrency();
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    ...options,
  }).format(value);
}

export function isLikelyDate(text: string): boolean {
  const t = text.trim();
  if (/^\d{1,2}[/-]\d{1,2}[/-]\d{2,4}$/.test(t)) return true;
  if (/^(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)[a-z]*\s+\d{1,2},?\s+\d{2,4}$/i.test(t)) {
    return true;
  }
  return false;
}

export function isLikelyDimension(text: string): boolean {
  return /\d+\s*[x×]\s*\d+/i.test(text);
}

export function isLikelyProjectNumber(text: string): boolean {
  const t = text.trim();
  if (/\b(?:INV|PO|SO|WO)[- ]?\d+/i.test(t)) return true;
  if (/^[A-Z]{1,4}-\d+$/.test(t)) return true;
  return false;
}

export function hasCurrencyToken(text: string): boolean {
  return HAS_CURRENCY.test(text);
}

export function hasPercentToken(text: string): boolean {
  return HAS_PERCENT.test(text);
}

function leftoverWords(text: string): string[] {
  const stripped = text
    .replace(new RegExp(CURRENCY_TOKEN.source, 'gi'), ' ')
    .replace(new RegExp(PERCENT_TOKEN.source, 'g'), ' ')
    .replace(/[\s,./\-+()[\]|:;·•–—\u2212]+/g, ' ')
    .trim();
  return stripped ? stripped.split(/\s+/) : [];
}

export function textIsPrimarilyFinancial(text: string): boolean {
  const trimmed = text.trim();
  if (!trimmed) return false;
  if (isLikelyDate(trimmed) || isLikelyDimension(trimmed) || isLikelyProjectNumber(trimmed)) {
    return false;
  }
  if (/\b(complete|progress|done|remaining)\b/i.test(trimmed)) return false;
  if (!hasCurrencyToken(trimmed) && !hasPercentToken(trimmed)) return false;
  return leftoverWords(trimmed).length <= 1;
}

export function financialKind(text: string): 'currency' | 'percent' {
  return hasCurrencyToken(text) ? 'currency' : 'percent';
}

function classListOf(el: Element): string[] {
  return el instanceof HTMLElement || el instanceof SVGElement
    ? Array.from(el.classList)
    : [];
}

export function isKnownMoneyClass(el: Element): boolean {
  const classes = classListOf(el);
  return classes.some(
    c =>
      (KNOWN_MONEY_CLASSES as readonly string[]).includes(c) ||
      (KNOWN_PERCENT_CLASSES as readonly string[]).includes(c),
  );
}

export function shouldSkipFinanceElement(el: Element): boolean {
  if (SKIP_TAGS.has(el.tagName)) return true;
  return Boolean(el.closest(SKIP_CLOSEST));
}

export function shouldMarkFinanceText(text: string, parent: Element): boolean {
  if (shouldSkipFinanceElement(parent)) return false;
  if (parent.closest('[data-crm-finance-wrap]')) return false;
  if (isKnownMoneyClass(parent) && (hasCurrencyToken(text) || hasPercentToken(text))) {
    return true;
  }
  return textIsPrimarilyFinancial(text);
}

export interface FinanceTextPart {
  text: string;
  kind?: 'currency' | 'percent';
}

export function splitFinancialTokens(text: string): FinanceTextPart[] {
  const hits: Array<{ start: number; end: number; kind: 'currency' | 'percent' }> = [];
  const currencyRe = new RegExp(CURRENCY_TOKEN.source, 'gi');
  let match: RegExpExecArray | null;
  while ((match = currencyRe.exec(text))) {
    const raw = match[0];
    const trimmed = raw.replace(/\s+$/, '');
    hits.push({ start: match.index, end: match.index + trimmed.length, kind: 'currency' });
  }
  const percentRe = new RegExp(PERCENT_TOKEN.source, 'g');
  while ((match = percentRe.exec(text))) {
    const start = match.index;
    const end = start + match[0].length;
    if (hits.some(hit => start < hit.end && end > hit.start)) continue;
    hits.push({ start, end, kind: 'percent' });
  }
  hits.sort((a, b) => a.start - b.start || b.end - a.end);

  const parts: FinanceTextPart[] = [];
  let cursor = 0;
  for (const hit of hits) {
    if (hit.start < cursor) continue;
    if (hit.start > cursor) parts.push({ text: text.slice(cursor, hit.start) });
    parts.push({ text: text.slice(hit.start, hit.end), kind: hit.kind });
    cursor = hit.end;
  }
  if (cursor < text.length) parts.push({ text: text.slice(cursor) });
  return parts.length ? parts : [{ text }];
}

export function shouldWrapFinanceTokens(text: string, parent: Element): boolean {
  if (shouldSkipFinanceElement(parent)) return false;
  if (parent.closest('[data-crm-finance-wrap]')) return false;
  if (parent.hasAttribute('data-crm-finance') && !parent.hasAttribute('data-crm-finance-wrap')) return false;
  if (isLikelyDate(text) || isLikelyDimension(text) || isLikelyProjectNumber(text)) return false;
  if (!hasCurrencyToken(text)) return false;
  return leftoverWords(text).length > 1;
}

function unmarkElement(el: Element): void {
  const stored = el.getAttribute('data-crm-finance-title');
  if (stored != null) {
    el.setAttribute('title', stored);
    el.removeAttribute('data-crm-finance-title');
  }
  el.removeAttribute('data-crm-finance');
  el.removeAttribute('data-crm-finance-kind');
  el.removeAttribute('data-crm-finance-wrap');
}

function unwrapFinanceWrap(el: Element): void {
  const parent = el.parentNode;
  if (!parent) {
    unmarkElement(el);
    return;
  }
  parent.replaceChild(document.createTextNode(el.textContent || ''), el);
}

function wrapFinanceTokensInTextNode(node: Text): void {
  const parent = node.parentElement;
  const text = node.nodeValue || '';
  if (!parent || !text) return;
  const parts = splitFinancialTokens(text);
  if (!parts.some(part => part.kind === 'currency' || part.kind === 'percent')) return;

  const frag = document.createDocumentFragment();
  for (const part of parts) {
    if (!part.kind) {
      frag.appendChild(document.createTextNode(part.text));
      continue;
    }
    const span = document.createElement('span');
    span.setAttribute('data-crm-finance', '1');
    span.setAttribute('data-crm-finance-kind', part.kind);
    span.setAttribute('data-crm-finance-wrap', '1');
    span.textContent = part.text;
    frag.appendChild(span);
  }
  parent.replaceChild(frag, node);
}

function markElement(el: Element, kind: 'currency' | 'percent'): void {
  el.setAttribute('data-crm-finance', '1');
  el.setAttribute('data-crm-finance-kind', kind);
  const title = el.getAttribute('title');
  if (title && (hasCurrencyToken(title) || hasPercentToken(title))) {
    if (!el.hasAttribute('data-crm-finance-title')) {
      el.setAttribute('data-crm-finance-title', title);
    }
    el.removeAttribute('title');
  }
}

function elementStillFinancial(el: Element): boolean {
  const text = el.textContent || '';
  return shouldMarkFinanceText(text, el);
}

export function clearFinanceSanitizeMarks(root: ParentNode = document.body): void {
  root.querySelectorAll('[data-crm-finance-wrap]').forEach(el => unwrapFinanceWrap(el));
  root.querySelectorAll('[data-crm-finance]').forEach(el => unmarkElement(el));
  if (root instanceof Element || root instanceof DocumentFragment || root instanceof Document) {
    root.normalize();
  }
}

export function applyFinanceSanitizeMarks(root: ParentNode = document.body): void {
  root.querySelectorAll('[data-crm-finance]').forEach(el => {
    if (el.hasAttribute('data-crm-finance-wrap')) {
      if (shouldSkipFinanceElement(el) || !hasCurrencyToken(el.textContent || '') && !hasPercentToken(el.textContent || '')) {
        unwrapFinanceWrap(el);
      }
      return;
    }
    if (shouldSkipFinanceElement(el) || !elementStillFinancial(el)) unmarkElement(el);
  });

  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  const toMark: Array<{ el: Element; kind: 'currency' | 'percent' }> = [];
  const toWrap: Text[] = [];

  while (walker.nextNode()) {
    const node = walker.currentNode as Text;
    const parent = node.parentElement;
    if (!parent) continue;
    const text = node.nodeValue || '';
    if (!text.trim()) continue;
    if (shouldMarkFinanceText(text, parent)) {
      toMark.push({ el: parent, kind: financialKind(text) });
      continue;
    }
    if (shouldWrapFinanceTokens(text, parent)) toWrap.push(node);
  }

  for (const { el, kind } of toMark) {
    markElement(el, kind);
  }
  for (const node of toWrap) {
    if (!node.parentElement) continue;
    wrapFinanceTokensInTextNode(node);
  }
}

export function startFinanceSanitizeObserver(root: ParentNode = document.body): () => void {
  let raf = 0;
  const schedule = () => {
    if (raf) return;
    raf = requestAnimationFrame(() => {
      raf = 0;
      applyFinanceSanitizeMarks(root);
    });
  };

  applyFinanceSanitizeMarks(root);
  const observer = new MutationObserver(schedule);
  observer.observe(root, { childList: true, subtree: true, characterData: true });

  return () => {
    observer.disconnect();
    if (raf) cancelAnimationFrame(raf);
    clearFinanceSanitizeMarks(root);
  };
}
